import { useAppSelector } from '@store';
import {
  selectAllFolders,
  selectFoldersLoading,
} from '@store/selectors/foldersSelectors';
import { Flex, Stack, Center, Loader } from '@mantine/core';

import { useEffect, useRef, useState, useTransition, useMemo } from 'react';
import { FolderCard, PaginationControls, FilterControls } from './components';
import {
  sortFolders,
  filterFolders,
  paginateFolders,
  getFoldersState,
  setFoldersState,
} from './utils';

export const UserFolders = ({ filter }: { filter?: any }) => {
  const allFolders = useAppSelector(selectAllFolders);
  const isLoading = useAppSelector(selectFoldersLoading);
  const isInitialLoad = useRef(true);
  const [isPending, startTransition] = useTransition();

  // Filter folders by category if filter has a category
  const folders = useMemo(() => {
    if (!filter?.category) {
      return allFolders;
    }
    return allFolders.filter(
      (folder: any) => folder.category === filter.category
    );
  }, [allFolders, filter?.category]);

  // Helper function to initialize filters state with defaults and saved state
  const initializeFiltersState = (filterId?: string) => {
    const savedState = getFoldersState(filterId);
    const defaultFilters: any = {
      isArchived: false,
      onlyWithData: false,
      backOfficeStatus: '',
      folderName: '',
      fileName: '',
      fileType: '',
      isPublished: '',
      isSigned: '',
      isLocked: '',
      fileStatus: '',
    };

    // Add custom field filters
    filter.fields.forEach((field: any) => {
      defaultFilters[field.name] = '';
    });

    // Merge saved filters with defaults to ensure all default fields are included
    // This ensures default fields are stored independently per filterId
    if (savedState?.filters) {
      return {
        ...defaultFilters,
        ...savedState.filters,
      };
    }

    return defaultFilters;
  };

  const [filtersState, setFiltersState] = useState(() =>
    initializeFiltersState(filter.id)
  );

  const [deferredFilters, setDeferredFilters] = useState(filtersState);

  const setFilters = (newFilters: any) => {
    setFiltersState(newFilters);
    startTransition(() => {
      setDeferredFilters(newFilters);
    });
  };

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getFoldersState(filter.id);
    return savedState?.currentPage || 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedState = getFoldersState(filter.id);
    // Validate itemsPerPage from saved state
    const saved = savedState?.itemsPerPage;
    return saved && saved > 0 ? saved : 10;
  });

  // Reload state when filter.id or filter.fields changes to ensure independent storage per filter
  useEffect(() => {
    const savedState = getFoldersState(filter.id);
    const newFiltersState = initializeFiltersState(filter.id);

    setFiltersState(newFiltersState);
    setDeferredFilters(newFiltersState);
    setCurrentPage(savedState?.currentPage || 1);
    const saved = savedState?.itemsPerPage;
    setItemsPerPage(saved && saved > 0 ? saved : 10);
    isInitialLoad.current = true;
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.id, filter.fields?.length]);

  // Listen for custom event to reload state when updated from notification
  useEffect(() => {
    const handleStateUpdate = (event: CustomEvent) => {
      if (event.detail?.filterId === filter.id) {
        const savedState = getFoldersState(filter.id);
        const newFiltersState = initializeFiltersState(filter.id);

        setFiltersState(newFiltersState);
        setDeferredFilters(newFiltersState);
        setCurrentPage(savedState?.currentPage || 1);
        const saved = savedState?.itemsPerPage;
        setItemsPerPage(saved && saved > 0 ? saved : 10);
      }
    };

    window.addEventListener(
      'foldersStateUpdated',
      handleStateUpdate as EventListener
    );

    return () => {
      window.removeEventListener(
        'foldersStateUpdated',
        handleStateUpdate as EventListener
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.id, filter.fields?.length]);

  // Save pagination state to localStorage (scoped by filterId)
  useEffect(() => {
    if (isInitialLoad.current) {
      return;
    }

    // Only save valid values
    if (itemsPerPage > 0 && currentPage > 0) {
      setFoldersState(
        {
          currentPage,
          itemsPerPage,
          filters: filtersState,
        },
        filter.id
      );
    }
  }, [currentPage, itemsPerPage, filtersState, filter.id]);

  // Check if any folder has files with isPublished property
  const hasIsPublishedFiles = useMemo(() => {
    return folders.some((folder) =>
      folder.files?.some((file: any) => file.hasOwnProperty('isPublished'))
    );
  }, [folders]);

  // Check if any folder has files with isSigned property
  const hasIsSignedFiles = useMemo(() => {
    return folders.some((folder) =>
      folder.files?.some((file: any) => file.hasOwnProperty('isSigned'))
    );
  }, [folders]);

  // Check if any folder has files with isLocked property
  const hasIsLockedFiles = useMemo(() => {
    return folders.some((folder) =>
      folder.files?.some((file: any) => file.hasOwnProperty('isLocked'))
    );
  }, [folders]);

  // Check if any folder has files with status property
  const hasFileStatus = useMemo(() => {
    return folders.some((folder) =>
      folder.files?.some((file: any) => file.hasOwnProperty('status'))
    );
  }, [folders]);

  // Apply filters and sorting with useMemo for better performance
  const filteredAndSortedFolders = useMemo(() => {
    // Apply filters first
    const filtered = filterFolders(folders, deferredFilters, filter.fields);

    // Then apply sorting
    return sortFolders(filtered, deferredFilters, filter.fields);
  }, [folders, deferredFilters, filter.fields]);

  // Apply pagination
  const { paginatedFolders, totalItems, totalPages } = paginateFolders(
    filteredAndSortedFolders,
    currentPage,
    itemsPerPage
  );

  // Auto-correct page if it's out of bounds (only after folders are loaded)
  useEffect(() => {
    if (
      !isLoading &&
      folders.length > 0 &&
      totalPages > 0 &&
      currentPage > totalPages
    ) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, isLoading, folders.length]);

  // Show loader while loading
  if (isLoading) {
    return (
      <Center h={250}>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md" my="md">
      <FilterControls
        filters={filtersState}
        customFieldBasedFilters={filter.fields}
        setFilters={setFilters}
        resetPage={() => setCurrentPage(1)}
        hasIsPublishedFiles={hasIsPublishedFiles}
        hasIsSignedFiles={hasIsSignedFiles}
        hasIsLockedFiles={hasIsLockedFiles}
        hasFileStatus={hasFileStatus}
      />

      <PaginationControls
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={setItemsPerPage}
      />

      {isPending ? (
        <Center h={250}>
          <Loader />
        </Center>
      ) : (
        <Flex direction="column" gap="md">
          {paginatedFolders.map((folder) => (
            <FolderCard
              key={folder.id}
              folder={folder}
              customFieldBasedFilters={filter.fields}
            />
          ))}
        </Flex>
      )}

      <PaginationControls
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={setItemsPerPage}
      />
    </Stack>
  );
};
